import React from 'react';
import PropTypes from 'prop-types';
import { action, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { withRouter, routerShape } from 'react-router';
import * as Sentry from '@sentry/react';

import { withConfig, withI18n } from 'spa/Components/Context';
import { BOOKING_BASE_URL } from 'utils/constants';
import autobind from 'autobind-decorator';
import isValidException from 'utils/isValidException';

import Button, {
  ButtonSizeTypes,
  ButtonType,
} from '@urbanmassage/component-library/lib/Button/Button';
import Link from '@urbanmassage/component-library/lib/Link/Link';

import AddressPickerSimpleRow from 'spa/Components/AddressPicker/AddressPickerSimpleRow';

import './treatment-single-book-section.less';

import Typography, {TypeVariants} from '@urbanmassage/component-library/lib/Typography/Typography';

@withRouter
@inject(({
  store: {
    basketNew,
    notifications,
    specialityAvailability,
    tracker,
  },
}) => (
  {
    basketNew,
    notifications,
    specialityAvailability,
    tracker,
  }
))
@observer
@withI18n
@withConfig
class TreatmentSingleBookSection extends React.Component {
  static propTypes = {
    basketNew: PropTypes.shape({
      basketHasAddress: PropTypes.bool,
      basketFormattedAddress: PropTypes.string,
      basketsAppointmentsAddress: PropTypes.shape({
        put: PropTypes.func.isRequired,
      }).isRequired,
      getBasketSummary: PropTypes.func.isRequired,
    }).isRequired,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    onClickBookButton: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    specialityAvailability: PropTypes.shape({
      load: PropTypes.func.isRequired,
    }).isRequired,
    specialityId: PropTypes.number.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
      trackCriteoEvent: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
    trackingLocation: PropTypes.string,
  }

  componentDidMount() {
    const {i18n} = this.context;
    const {
      basketNew,
      notifications,
      specialityAvailability,
      specialityId,
      tracker,
      trackingCategory,
      trackingLocation,
    } = this.props;
    const {getUnavailableReason} = this;

    this.listeners = [
      reaction(
        () => basketNew.basketHasAddress,
        (basketHasAddress) => {
          if (basketHasAddress) {
            specialityAvailability.load(specialityId, {
              appointment_id: 'active',
            }).then((response) => {
              this.isBookable = true;

              if (response.blockReason) {
                this.blockReason = getUnavailableReason(response.blockReason);

                tracker.track('Speciality not available for basket', {
                  category: trackingCategory,
                  page: trackingLocation,
                  speciality_id: specialityId,
                  blockReason: response.blockReason,
                });
              } else {
                tracker.track('Speciality available for basket', {
                  category: trackingCategory,
                  page: trackingLocation,
                  speciality_id: specialityId,
                });
              }
            }).catch((error) => {
              if (__DEV__) {
                console.error(error);
              }

              if (error.json && error.json.httpCode !== 404) {
                notifications.add({
                  id: 'getSpecialityAvailabilityError',
                  type: 'error',
                  title: i18n.__error(error),
                });

                tracker.track('Error getting speciality availability for basket', {
                  category: trackingCategory,
                  page: trackingLocation,
                  speciality_id: specialityId,
                });
              }

              // This is unexpected so record it in Sentry
              if (isValidException(error)) {
                Sentry.captureException(error);
              } else {
                Sentry.captureMessage('Unhandled error getting speciality availability in the speciality details page');
              }
            });
          }
        },
        {
          name: 'Check speciality availability for basket',
          fireImmediately: true,
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable basketHasAddress = false;
  @observable blockReason;
  @observable isBookable = false;
  @observable isLoading = false;

  @autobind
  getUnavailableReason(code) {
    const {i18n} = this.context;

    if (!code) return null;

    switch (code) {
    case 'CANNOT_COMBINE_BASKET_WITH_DIGITAL':
    case 'CANNOT_COMBINE_BASKET_WITH_PHYSICAL':
      return i18n._x('Book online and in-person treatments separately', 'Unavailable speciality text');
    case 'NO_AVAILABILITY_WITH_BASKET':
      return i18n._x('Not available with your basket', 'Unavailable speciality text');
    case 'NO_AVAILABILITY_FOR_SELECTED_WORKER':
      return i18n._x('Your selected pro doesn\'t offer this treatment', 'Unavailable speciality text');
    default:
      return i18n._x('Not available with your basket', 'Unavailable speciality text');
    }
  }

  @autobind
  @action('onClickUseAddress - TreatmentSingleBookSection')
  onClickUseAddress(selectedAddress) {
    const {i18n} = this.context;
    const {
      basketNew,
      basketNew: {
        basketsAppointmentsAddress,
      },
      notifications,
      router,
      tracker,
      trackingCategory,
      trackingLocation,
    } = this.props;

    const trackingData = {
      category: trackingCategory,
      label: selectedAddress,
    };

    if (trackingLocation) {
      trackingData.data = {
        page: trackingLocation,
      };
    }

    tracker.track('Clicked add address to basket in speciality details page', trackingData);

    this.isLoading = true;

    basketsAppointmentsAddress.put('', selectedAddress)
      .then((response) => {
        this.isLoading = false;
        basketNew.getBasketSummary()
          .catch((err) => {
            if (__DEV__) {
              console.error(err);
            }
          });
      }, (error) => {
        runInAction(() => {
          if (error.json && error.json.code === 'ADDRESS_NOT_SERVICED') {
            router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/address?notServiced=true`);
          } else {
            if (__DEV__) {
              console.error(error);
            }
            notifications.add({
              id: 'addAddressBasketError',
              type: 'error',
              title: i18n.__error(error),
            });

            // This is unexpected so record it in Sentry
            if (isValidException(error)) {
              Sentry.captureException(error);
            } else {
              Sentry.captureMessage('Unhandled error adding address to basket appointment');
            }
          }

          this.isLoading = false;
        });
      }).catch((err) => {
        if (__DEV__) {
          console.error(err);
        }

        this.isLoading = false;

        // This is unexpected so record it in Sentry
        if (isValidException(err)) {
          Sentry.captureException(err);
        } else {
          Sentry.captureMessage('Unhandled error adding address to basket appointment');
        }
      });
  }

  render() {
    const { i18n } = this.context;
    const {
      basketNew,
      onClickBookButton,
    } = this.props;
    const {
      blockReason,
      isBookable,
      isLoading,
      onClickUseAddress,
    } = this;

    return (
      <div className="treatment-single-book-section" data-testid="treatment-single-book-section">
        {basketNew.basketHasAddress ? (
          <React.Fragment>
            <Typography
              align="center"
              component={TypeVariants.body}
              data-testid="treatment-single-book-section-address-info"
              margin="1.5rem 0"
              variant={TypeVariants.titleXS}
            >
              {i18n._x('Booking at %(formattedAddress)s', 'Treatment details page book section', {
                formattedAddress: basketNew.basketFormattedAddress,
              })}
            </Typography>
            {isBookable && !blockReason ? (
              <React.Fragment>
                <Button
                  block
                  data-testid="treatment-single-book-section-book-button"
                  data-qa="treatment-single-book-section-book-button"
                  onClick={onClickBookButton}
                  size={ButtonSizeTypes.default}
                  type={ButtonType.button}
                >
                  {i18n._x('Confirm', 'treatment book now button')}
                </Button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Typography
                  align="center"
                  component={TypeVariants.body}
                  data-testid="treatment-single-book-section-block-reason"
                  margin="1.5rem 0 0"
                  theme="standout"
                  variant={TypeVariants.titleXS}
                >
                  {blockReason}
                </Typography>
                <Typography
                  align="center"
                  component={TypeVariants.body}
                  data-testid="treatment-single-book-section-block-check-basket"
                  margin="0 0 1rem"
                  variant={TypeVariants.body}
                >
                  ({i18n.__react('Check the contents of %(basketLink)s', {
                    basketLink: <Link to={`/${i18n.lc}/${BOOKING_BASE_URL}/basket`} text={i18n._x('your basket', 'Treatment details page book section')} />,
                  }, 'Treatment details page book section')})
                </Typography>
              </React.Fragment>
            )}
          </React.Fragment>
        ) : (
          <AddressPickerSimpleRow
            customButtonTextDesktop={i18n._x('Select', 'Treatment details page address picker button')}
            customButtonTextMobile={i18n._x('Select', 'Treatment details page address picker button')}
            isLoading={isLoading}
            onClickSelectAddress={onClickUseAddress}
            stackButton
          />
        )}
      </div>
    );
  }
}

export default TreatmentSingleBookSection;



// WEBPACK FOOTER //
// ./spa/Pages/Treatments/TreatmentSingleBookSection.jsx